import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { selectAdminToken } from "../../admin/sessionSlice";
import { fetchThemeData } from "../../admin/themeSlice";
import { ensureGoogleFont } from "../../admin/components/TextStyleControls";
import { setBackButtonUrl } from "../uiSlice";
import { GAME_UPLOAD_PATH } from "../../utils/playerNavigation";
import { s3ThemeAssetUrl } from "../../utils/themeAssets";
import { apiUrl } from "../../utils/apiUrl";
import "../../components/stage/stageShell.css";
import "./mosaicWall.css";

const MOSAIC_FONT = "Arial, Helvetica, sans-serif";
const DEFAULT_WORD = "India";
const DEFAULT_TITLE = "Photo mosaic";
const DEFAULT_DESKTOP = "16rem";
const DEFAULT_MOBILE = "4rem";
const DEFAULT_TITLE_STYLE = {
  font_family: "",
  font_size_desktop: "1.5rem",
  font_size_mobile: "1.125rem",
  color: "#111111",
};

function shuffleArray(array) {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
}

function isMosaicEnabled(themeData) {
  return String(themeData?.mosaic_enabled ?? "false").trim().toLowerCase() === "true";
}

function toRemCss(raw, fallback, maxRem = 4) {
  const fb = String(fallback);
  if (raw == null || String(raw).trim() === "") return fb;
  const s = String(raw).trim().toLowerCase();
  if (/^\d+(\.\d+)?rem$/.test(s) || /^\d+(\.\d+)?px$/.test(s) || /^\d+(\.\d+)?em$/.test(s)) {
    return s;
  }
  if (/^\d+(\.\d+)?$/.test(s)) {
    const n = Number.parseFloat(s);
    if (!Number.isFinite(n) || n <= 0) return fb;
    // Legacy px values from older admin UI (e.g. 24)
    if (n > maxRem) return `${Math.round((n / 16) * 1000) / 1000}rem`;
    return `${n}rem`;
  }
  return fb;
}

function parseTitleStyle(raw) {
  let parsed = raw;
  if (typeof raw === "string" && raw.trim()) {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return { ...DEFAULT_TITLE_STYLE };
    }
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return { ...DEFAULT_TITLE_STYLE };
  }
  return {
    font_family:
      parsed.font_family != null ? String(parsed.font_family) : DEFAULT_TITLE_STYLE.font_family,
    font_size_desktop: toRemCss(
      parsed.font_size_desktop,
      DEFAULT_TITLE_STYLE.font_size_desktop,
      4
    ),
    font_size_mobile: toRemCss(
      parsed.font_size_mobile,
      DEFAULT_TITLE_STYLE.font_size_mobile,
      3
    ),
    color: parsed.color || parsed.text_color || DEFAULT_TITLE_STYLE.color,
  };
}

export default function MosaicWall() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { data: themeData, status, error: themeError } = useSelector((s) => s.theme);
  const user = useSelector((s) => s.auth.user);
  const adminToken = useSelector(selectAdminToken);

  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth < 768
  );
  const containerRef = useRef(null);

  const word = String(themeData?.mosaic_word || DEFAULT_WORD)
    .trim()
    .toUpperCase();
  const pageTitle = String(themeData?.mosaic_title ?? DEFAULT_TITLE)
    .trim()
    .slice(0, 100);
  const titleStyle = useMemo(
    () => parseTitleStyle(themeData?.mosaic_title_style),
    [themeData?.mosaic_title_style]
  );
  const fillColor = themeData?.text_color || "#1a2b6d";

  useEffect(() => {
    dispatch(setBackButtonUrl(GAME_UPLOAD_PATH));
  }, [dispatch]);

  useEffect(() => {
    if (!adminToken || !user?.userId) return;
    fetch(apiUrl("/mosaicPlay"), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${adminToken}`,
      },
      body: JSON.stringify({ userId: user.userId }),
    }).catch(() => {});
  }, [adminToken, user?.userId]);

  useEffect(() => {
    if (status === "idle") {
      dispatch(fetchThemeData({ themeId: null }));
    }
  }, [status, dispatch]);

  useEffect(() => {
    if (!themeData) return;
    if (!isMosaicEnabled(themeData)) {
      navigate("/gallery", { replace: true });
    }
  }, [themeData, navigate]);

  useEffect(() => {
    if (titleStyle.font_family) ensureGoogleFont(titleStyle.font_family);
  }, [titleStyle.font_family]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const titleInlineStyle = useMemo(() => {
    const size = isMobile
      ? titleStyle.font_size_mobile || DEFAULT_TITLE_STYLE.font_size_mobile
      : titleStyle.font_size_desktop || DEFAULT_TITLE_STYLE.font_size_desktop;
    return {
      color: titleStyle.color || DEFAULT_TITLE_STYLE.color,
      fontFamily: titleStyle.font_family || undefined,
      fontSize: size,
    };
  }, [isMobile, titleStyle]);

  const parseToPx = (val, fallback) => {
    if (val == null) return fallback;
    if (typeof val === "number" && Number.isFinite(val) && val > 0) return Math.floor(val);
    const raw = String(val).trim().toLowerCase();
    if (!raw) return fallback;

    const m = raw.match(/^(\d+(?:\.\d+)?)(px|rem|em)?$/);
    if (!m) return fallback;
    const num = parseFloat(m[1]);
    if (!Number.isFinite(num) || num <= 0) return fallback;

    const unit = m[2] || "px";
    if (unit === "px") return Math.floor(num);

    const rootPx = parseFloat(getComputedStyle(document.documentElement).fontSize || "16") || 16;
    if (unit === "rem") return Math.floor(num * rootPx);
    if (unit === "em") {
      const el = containerRef.current || document.body;
      const elPx = parseFloat(getComputedStyle(el).fontSize || String(rootPx)) || rootPx;
      return Math.floor(num * elPx);
    }

    return fallback;
  };

  const loadMosaic = useCallback(async () => {
    if (!adminToken) {
      setImages([]);
      return;
    }
    try {
      const resp = await fetch(apiUrl("/talentGallery"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({
          userId: user?.userId || "",
          category: "All",
          sort: "newest",
        }),
      });
      const payload = await resp.json();
      const uploads =
        resp.ok && payload?.success && Array.isArray(payload.uploads) ? payload.uploads : [];
      setImages(
        uploads
          .filter((u) => u.mediaType === "image" && u.uploadFile)
          .map((u) => s3ThemeAssetUrl(u.uploadFile))
      );
    } catch (e) {
      console.error("mosaicWall", e);
      setImages([]);
    }
  }, [adminToken, user?.userId]);

  useEffect(() => {
    loadMosaic();
  }, [loadMosaic]);

  const buildMosaic = useCallback(() => {
    const el = containerRef.current;
    if (!el || !word) return;

    while (el.firstChild) el.removeChild(el.firstChild);

    const width = el.offsetWidth;
    const height = el.offsetHeight;
    if (!width || !height) return;

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "black";

    const cleanWord = word.trim();
    const firstSpaceIndex = cleanWord.indexOf(" ");
    const lines =
      firstSpaceIndex !== -1
        ? [cleanWord.substring(0, firstSpaceIndex), cleanWord.substring(firstSpaceIndex + 1)]
        : [cleanWord];

    const maxTextWidth = width * 0.92;
    const maxTextHeight = height * 0.9;
    const REF = 100;
    ctx.font = `bold ${REF}px ${MOSAIC_FONT}`;
    const widestRef = Math.max(...lines.map((l) => ctx.measureText(l).width)) || 1;
    const widthLimited = (maxTextWidth / widestRef) * REF;
    const heightLimited = maxTextHeight / (lines.length * 1.2);
    const autoFitFontSize = Math.max(12, Math.floor(Math.min(widthLimited, heightLimited)));

    const mobileViewport = window.innerWidth < 768;
    const adminFontRaw = mobileViewport
      ? themeData?.mosaic_font_size_mobile ?? DEFAULT_MOBILE
      : themeData?.mosaic_font_size_desktop ?? DEFAULT_DESKTOP;
    const adminFontPx = parseToPx(adminFontRaw, null);
    const fontSize =
      adminFontPx != null ? Math.max(12, Math.min(autoFitFontSize, adminFontPx)) : autoFitFontSize;

    ctx.font = `bold ${fontSize}px ${MOSAIC_FONT}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    if (lines.length > 1) {
      const lineHeight = fontSize * 1.2;
      const totalHeight = lines.length * lineHeight;
      lines.forEach((line, index) => {
        ctx.fillText(
          line,
          width / 2,
          height / 2 - totalHeight / 2 + index * lineHeight + lineHeight / 2
        );
      });
    } else {
      ctx.fillText(cleanWord, width / 2, height / 2);
    }

    const dataURL = canvas.toDataURL("image/png");
    el.style.webkitMaskImage = `url(${dataURL})`;
    el.style.maskImage = `url(${dataURL})`;

    if (images.length === 0) {
      el.style.backgroundColor = fillColor;
      return;
    }
    el.style.backgroundColor = "";

    const cellSize = window.innerWidth < 768 ? 30 : 40;
    el.style.gridTemplateColumns = `repeat(auto-fill, ${cellSize}px)`;
    el.style.gridAutoRows = `${cellSize}px`;
    const cols = Math.floor(width / cellSize);
    const rows = Math.floor(height / cellSize);
    const totalTiles = cols * rows;
    if (totalTiles <= 0) return;

    const positions = [];
    for (let i = 0; i < totalTiles; i++) positions.push(i);
    shuffleArray(positions);

    const fragment = document.createDocumentFragment();
    for (let i = 0; i < totalTiles; i++) {
      const img = document.createElement("img");
      img.src = images[i % images.length];
      img.alt = "";
      img.loading = "lazy";
      img.style.gridArea = `${Math.floor(positions[i] / cols) + 1} / ${(positions[i] % cols) + 1} / auto / auto`;
      img.style.animationDelay = `${i * 2}ms`;
      fragment.appendChild(img);
    }
    el.appendChild(fragment);
  }, [
    word,
    fillColor,
    images,
    themeData?.mosaic_font_size_desktop,
    themeData?.mosaic_font_size_mobile,
  ]);

  useEffect(() => {
    if (!word || !isMosaicEnabled(themeData)) {
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    let cancelled = false;
    let id;
    const run = () => {
      id = setTimeout(() => {
        if (cancelled) return;
        buildMosaic();
        setLoading(false);
      }, 100);
    };
    if (document.fonts && document.fonts.load) {
      document.fonts
        .load(`bold 100px ${MOSAIC_FONT}`)
        .then(() => document.fonts.ready)
        .then(run)
        .catch(run);
    } else {
      run();
    }
    return () => {
      cancelled = true;
      clearTimeout(id);
    };
  }, [buildMosaic, word, themeData]);

  useEffect(() => {
    if (!word) return undefined;
    let resizeTimer;
    const onResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => buildMosaic(), 200);
    };
    window.addEventListener("resize", onResize);
    return () => {
      clearTimeout(resizeTimer);
      window.removeEventListener("resize", onResize);
    };
  }, [buildMosaic, word]);

  if (status === "failed") {
    return (
      <div className="stage-shell mosaic-page">
        <div className="stage-inner">
          <p style={{ textAlign: "center" }}>
            Could not load mosaic.{themeError ? ` (${themeError})` : ""}
          </p>
          <button
            type="button"
            className="stage-cta"
            style={{ display: "block", margin: "16px auto" }}
            onClick={() => dispatch(fetchThemeData({ themeId: null }))}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!themeData || !isMosaicEnabled(themeData)) {
    return (
      <div className="stage-shell mosaic-page">
        <div className="stage-inner">
          <div className="quiz-loader-container">
            <div className="quiz-loader" />
          </div>
        </div>
      </div>
    );
  }

  if (!word) return null;

  return (
    <div className="stage-shell mosaic-page">
      <div className="stage-inner mosaic-inner">
        <div className="mosaic-header">
          {pageTitle ? (
            <h1 className="mosaic-title" style={titleInlineStyle}>
              {pageTitle}
            </h1>
          ) : (
            <span />
          )}
          <button type="button" className="mosaic-back-btn" onClick={() => navigate(GAME_UPLOAD_PATH)}>
            Back to upload
          </button>
        </div>

        <div className="mosaic-word">
          <div className="masked-letter" ref={containerRef} data-letter={word} />

          {loading ? (
            <div className="mosaic-loading">
              <div className="mosaic-spinner" />
              <p>Creating your mosaic...</p>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
